import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, Megaphone, Download } from 'lucide-react';
import { personalInfo } from '../../data/mock';

const iconMap = { Brain, Zap, Megaphone };

const GITHUB_ACTIVITY_CACHE_KEY = 'gh-last-push-v1';
const GITHUB_ACTIVITY_CACHE_TTL = 1000 * 60 * 60 * 6; // 6 hours

const timeAgo = (isoDate) => {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
};

const useGithubActivity = (username) => {
  const [label, setLabel] = useState('Actively shipping on GitHub');

  useEffect(() => {
    let cancelled = false;

    const cached = sessionStorage.getItem(GITHUB_ACTIVITY_CACHE_KEY);
    if (cached) {
      try {
        const { label: cachedLabel, savedAt } = JSON.parse(cached);
        if (Date.now() - savedAt < GITHUB_ACTIVITY_CACHE_TTL) {
          setLabel(cachedLabel);
          return;
        }
      } catch {
        // ignore malformed cache, fall through to a fresh fetch
      }
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    fetch(`https://api.github.com/users/${username}/repos?sort=pushed&per_page=1`, {
      signal: controller.signal,
      headers: { Accept: 'application/vnd.github+json' },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('GitHub API error'))))
      .then((repos) => {
        if (cancelled || !Array.isArray(repos) || repos.length === 0) return;
        const pushedAt = repos[0].pushed_at;
        const freshLabel = `Last pushed ${timeAgo(pushedAt)}`;
        setLabel(freshLabel);
        sessionStorage.setItem(GITHUB_ACTIVITY_CACHE_KEY, JSON.stringify({ label: freshLabel, savedAt: Date.now() }));
      })
      .catch(() => {
        // Network error, rate limit, or timeout: keep the static fallback label
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [username]);

  return label;
};

const AboutSection = () => {
  const githubActivityLabel = useGithubActivity('nihanthnaidu007');

  return (
    <section id="about" className="py-24 relative z-10">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="section-label block mb-12"
        >
          // ABOUT ME
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-3xl lg:text-4xl font-bold text-[#F0F4FF] mb-12"
        >
          About Me
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="relative">
              {/* Rotating dashed border */}
              <div className="absolute inset-[-8px] rounded-full border-2 border-dashed border-[rgba(0,212,255,0.3)] animate-spin-slow" />
              {/* Glowing ring */}
              <div className="absolute inset-[-4px] rounded-full border-2 border-[#00D4FF] glow-cyan" />
              {/* Square headshot fills the frame; circle clips corners (typically white) so shoulders stay natural */}
              <div className="relative w-64 h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden bg-white">
                <img
                  src={personalInfo.photoUrl}
                  alt={personalInfo.fullName}
                  className="h-full w-full object-cover object-center"
                />
                {/* Vignette blends the white photo backdrop into the dark theme */}
                <div
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{ boxShadow: 'inset 0 0 48px 14px rgba(8,12,20,0.45)' }}
                />
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p className="text-lg text-[#C0C8D8] leading-relaxed mb-8">
              {personalInfo.bio}
            </p>

            {/* What I Bring */}
            <div className="space-y-4 mb-8">
              {personalInfo.whatIBring.map((item, i) => {
                const Icon = iconMap[item.icon];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.15 }}
                    className="glass-card glass-card-hover rounded-xl p-4 flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[rgba(0,212,255,0.1)] flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-[#00D4FF]" />
                    </div>
                    <div>
                      <h4 className="font-display font-semibold text-[#F0F4FF] mb-1">{item.title}</h4>
                      <p className="font-mono text-sm text-[#8892A4]">{item.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Download Resume */}
            <a
              href={personalInfo.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[#00D4FF] text-[#00D4FF] font-mono text-sm hover:bg-[#00D4FF] hover:text-[#080C14] transition-colors duration-300 group"
            >
              <Download size={16} className="group-hover:animate-bounce" />
              Download Resume
            </a>

            {/* GitHub activity signal */}
            <a
              href={personalInfo.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 mt-4 w-fit group"
            >
              <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse flex-shrink-0" />
              <span className="font-mono text-xs text-[#4A6A4A] group-hover:text-[#39FF14] transition-colors duration-300">
                {githubActivityLabel}
              </span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
